import { useState, useMemo, useCallback } from 'react';
import { IFormField } from '../../types/suggestions/form';
import { User } from '../../types/common/user';

export type FormState = Record<string, string | number | Date | { results: string[] } | null>;
export type ErrorState = Record<string, boolean>;

export interface UseDynamicFormStateReturn {
    formState: FormState;
    errorState: ErrorState;
    updateField: (fieldId: string, value: any) => void;
    resetForm: () => void;
    getFieldValue: (fieldId: string) => any;
    hasFieldError: (fieldId: string) => boolean;
    validateForm: () => boolean;
    validateField: (fieldId: string) => boolean;
}

export const useDynamicFormState = (fields: IFormField[], currentUser?: User): UseDynamicFormStateReturn => {
    const prepopulateValue = useCallback((value: any): any => {
        if (!currentUser) return value;
        if (typeof value === 'string') {
            return value
                .replace('{UserID}', currentUser.SPID.toString())
                .replace('{UserRegionID}', currentUser.RegionID.toString())
                .replace('{UserEmail}', currentUser.Email);
        } else if (typeof value === 'object' && value !== null) {
            if (value.results && Array.isArray(value.results)) {
                return {
                    results: value.results.map((item: any) => prepopulateValue(item))
                };
            }
            const prepopulated: any = {};
            for (const key in value) {
                prepopulated[key] = prepopulateValue(value[key]);
            }
            return prepopulated;
        }
        return value;
    }, [currentUser]);

    const initialState = useMemo(() => {
        const state: FormState = {};

        fields.forEach(field => {
            if (field.type === 'Internal') {
                // Internal fields have predefined values, prepopulated with user context
                state[field.id] = prepopulateValue(field.value);
            } else if (field.type === 'Date') {
                // Date fields start as null
                state[field.id] = null;
            } else if (field.type === 'Number') {
                // Number fields start as empty string
                state[field.id] = '';
            } else if (field.type === 'Text') {
                // Text fields start as empty string
                state[field.id] = '';
            } else if (field.type === 'Choice') {
                // Choice fields start as empty string
                state[field.id] = '';
            } else if (field.type === 'List') {
                // List fields start as empty string; value stored is the option key
                state[field.id] = '';
            }
        });

        return state;
    }, [fields, prepopulateValue]);

    const [formState, setFormState] = useState<FormState>(initialState);
    const [errorState, setErrorState] = useState<ErrorState>({});

    const validateField = (fieldId: string): boolean => {
        const field = fields.find(f => f.id === fieldId);
        if (!field || field.type === 'Internal') return true;

        const value = formState[fieldId];

        // Check required fields
        if (field.required) {
            if (value === null || value === undefined || value === '') {
                return false;
            }
        }

        // Additional type-specific validations
        if (field.type === 'Date' && field.required) {
            if (!value || !(value instanceof Date)) {
                return false;
            }
        }

        if (field.type === 'Number' && value !== '' && value !== null) {
            const numValue = Number(value);
            if (isNaN(numValue)) return false;
            if (field.min !== undefined && numValue < field.min) return false;
            if (field.max !== undefined && numValue > field.max) return false;
        }

        // Text field regex validation
        if (field.type === 'Text' && value && typeof value === 'string') {
            const textField = field as any; // Type assertion to access regex property
            if (textField.regex && Array.isArray(textField.regex) && textField.regex.length > 0) {
                // Test against the first regex pattern only
                const pattern = textField.regex[0];
                try {
                    const regex = new RegExp(pattern);
                    if (!regex.test(value)) return false;
                } catch (error) {
                    console.error('Invalid regex pattern:', pattern, error);
                    // Don't fail validation for invalid regex patterns
                }
            }
        }

        return true;
    };

    const validateForm = (): boolean => {
        const errors: ErrorState = {};
        let isValid = true;

        fields.forEach(field => {
            if (field.type !== 'Internal') {
                const fieldValid = validateField(field.id);
                errors[field.id] = !fieldValid;
                if (!fieldValid) {
                    isValid = false;
                }
            }
        });

        setErrorState(errors);
        return isValid;
    };

    const updateField = (fieldId: string, value: any) => {
        setFormState(prev => ({
            ...prev,
            [fieldId]: value
        }));

        // Clear error when user updates field
        if (errorState[fieldId]) {
            setErrorState(prev => ({
                ...prev,
                [fieldId]: false
            }));
        }
    };

    const resetForm = () => {
        setFormState(initialState);
        setErrorState({});
    };

    const getFieldValue = (fieldId: string) => {
        return formState[fieldId];
    };

    const hasFieldError = (fieldId: string): boolean => {
        return errorState[fieldId] || false;
    };

    return {
        formState,
        errorState,
        updateField,
        resetForm,
        getFieldValue,
        hasFieldError,
        validateForm,
        validateField
    };
};
